import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import SurveyTemplate from "./SurveyTemplate";
import "../styles/components/MultiPageSurvey.css";
import FEATURE_FLAGS from "../config/featureFlags";

const SurveyPage4 = ({
  surveyData,
  updateSurveyData,
  onNext,
  onBack,
  onNavigationBack,
  currentSection,
  initialQuestionIndex = 0,
  updatePageState,
  pageNumber,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [currentQuestion, setCurrentQuestion] = useState(initialQuestionIndex);
  const [localData, setLocalData] = useState({
    chronicDiseases: surveyData.chronicDiseases || [],
    bloodSugarStatus: surveyData.bloodSugarStatus || "",
    cholesterolStatus: surveyData.cholesterolStatus || "",
    bloodPressureStatus: surveyData.bloodPressureStatus || "",
    smokingHistory: surveyData.smokingHistory || "",
    cigarettesPerDay: surveyData.cigarettesPerDay || "",
    livedWithSmoker: surveyData.livedWithSmoker || "",
    radiationExposure: surveyData.radiationExposure || "",
  });

  const updatePageStateRef = useRef(updatePageState);
  updatePageStateRef.current = updatePageState;
  const lastSyncedQuestion = useRef(null);
  useEffect(() => {
    if (lastSyncedQuestion.current === currentQuestion || !updatePageStateRef.current || !pageNumber) return;
    lastSyncedQuestion.current = currentQuestion;
    updatePageStateRef.current(pageNumber, currentQuestion);
  }, [currentQuestion, pageNumber]);

  const questions = [
    {
      id: "chronicDiseases",
      question: t("survey.page4.chronicDiseases.question"),
      type: "multiSelect",
      options: [
        {
          value: "cardiovascular",
          label: t("survey.page4.chronicDiseases.options.cardiovascular"),
        },
        {
          value: "lung",
          label: t("survey.page4.chronicDiseases.options.lung"),
        },
        {
          value: "diabetes",
          label: t("survey.page4.chronicDiseases.options.diabetes"),
        },
        {
          value: "gastrointestinal",
          label: t("survey.page4.chronicDiseases.options.gastrointestinal"),
        },
        {
          value: "liver",
          label: t("survey.page4.chronicDiseases.options.liver"),
        },
        {
          value: "thyroid",
          label: t("survey.page4.chronicDiseases.options.thyroid"),
        },
        {
          value: "breast",
          label: t("survey.page4.chronicDiseases.options.breast"),
        },
        {
          value: "cervical",
          label: t("survey.page4.chronicDiseases.options.cervical"),
        },
        {
          value: "uterine",
          label: t("survey.page4.chronicDiseases.options.uterine"),
        },
        {
          value: "endometriosis",
          label: t("survey.page4.chronicDiseases.options.endometriosis"),
        },
        {
          value: "pcos",
          label: t("survey.page4.chronicDiseases.options.pcos"),
        },
        {
          value: "prostate",
          label: t("survey.page4.chronicDiseases.options.prostate"),
        },
        {
          value: "testicular",
          label: t("survey.page4.chronicDiseases.options.testicular"),
        },
        {
          value: "none",
          label: t("survey.page4.chronicDiseases.options.none"),
        },
      ],
      info: t("survey.page4.chronicDiseases.info"),
    },
    {
      id: "bloodSugarStatus",
      question: t("survey.page4.bloodSugar.question"),
      type: "select",
      options: [
        { value: "normal", label: t("survey.page4.bloodSugar.options.normal") },
        { value: "prediabetic", label: t("survey.page4.bloodSugar.options.prediabetic") },
        { value: "diabetic", label: t("survey.page4.bloodSugar.options.diabetic") },
        { value: "unsure", label: t("survey.page4.bloodSugar.options.unsure") },
      ],
      info: t("survey.page4.bloodSugar.info"),
    },
    {
      id: "cholesterolStatus",
      question: t("survey.page4.cholesterol.question"),
      type: "select",
      options: [
        { value: "normal", label: t("survey.page4.cholesterol.options.normal") },
        { value: "borderline", label: t("survey.page4.cholesterol.options.borderline") },
        { value: "high", label: t("survey.page4.cholesterol.options.high") },
        { value: "unsure", label: t("survey.page4.cholesterol.options.unsure") },
      ],
      info: t("survey.page4.cholesterol.info"),
    },
    {
      id: "bloodPressureStatus",
      question: t("survey.page4.bloodPressure.question"),
      type: "select",
      options: [
        { value: "normal", label: t("survey.page4.bloodPressure.options.normal") },
        { value: "elevated", label: t("survey.page4.bloodPressure.options.elevated") },
        { value: "high", label: t("survey.page4.bloodPressure.options.high") },
        { value: "unsure", label: t("survey.page4.bloodPressure.options.unsure") },
      ],
      info: t("survey.page4.bloodPressure.info"),
    },
    {
      id: "smokingHistory",
      question: t("survey.page4.smokingHistory.question"),
      type: "number",
      placeholder: t("survey.page4.smokingHistory.placeholder"),
      unit: t("survey.page4.smokingHistory.unit"),
      info: t("survey.page4.smokingHistory.info"),
    },
    {
      id: "cigarettesPerDay",
      question: t("survey.page4.cigarettesPerDay.question"),
      type: "number",
      placeholder: t("survey.page4.cigarettesPerDay.placeholder"),
      unit: t("survey.page4.cigarettesPerDay.unit"),
      info: t("survey.page4.cigarettesPerDay.info"),
    },
    {
      id: "livedWithSmoker",
      question: t("survey.page4.livedWithSmoker.question"),
      type: "select",
      options: [
        { value: "yes", label: t("survey.page4.livedWithSmoker.options.yes") },
        { value: "no", label: t("survey.page4.livedWithSmoker.options.no") },
      ],
      info: t("survey.page4.livedWithSmoker.info"),
    },
    {
      id: "radiationExposure",
      question: t("survey.page4.radiationExposure.question"),
      type: "select",
      options: [
        {
          value: "yes",
          label: t("survey.page4.radiationExposure.options.yes"),
        },
        { value: "no", label: t("survey.page4.radiationExposure.options.no") },
        {
          value: "unsure",
          label: t("survey.page4.radiationExposure.options.unsure"),
        },
      ],
      info: t("survey.page4.radiationExposure.info"),
    },
  ].filter((q) => {
    // Hide blood sugar, cholesterol, blood pressure questions when IMPROVED_VITALS_FLOW is off
    if (!FEATURE_FLAGS.IMPROVED_VITALS_FLOW) {
      return !["bloodSugarStatus", "cholesterolStatus", "bloodPressureStatus"].includes(q.id);
    }
    return true;
  });

  const handleOptionSelect = (value) => {
    const currentQ = questions[currentQuestion];
    if (currentQ.type === "multiSelect") {
      setLocalData((prev) => {
        let currentDiseases = [...prev.chronicDiseases];

        if (value === "none") {
          // If "none" is selected, clear all other selections
          if (currentDiseases.includes("none")) {
            // Remove "none" if already selected
            currentDiseases = currentDiseases.filter((item) => item !== "none");
          } else {
            // Select only "none" and clear everything else
            currentDiseases = ["none"];
          }
        } else {
          // If a specific disease is selected
          if (currentDiseases.includes("none")) {
            // Remove "none" if it was previously selected
            currentDiseases = currentDiseases.filter((item) => item !== "none");
          }

          const index = currentDiseases.indexOf(value);
          if (index > -1) {
            currentDiseases.splice(index, 1);
          } else {
            currentDiseases.push(value);
          }
        }

        return {
          ...prev,
          [currentQ.id]: currentDiseases,
        };
      });
    } else {
      setLocalData((prev) => ({
        ...prev,
        [currentQ.id]: value,
      }));

      // Auto-advance for single-select questions after a brief delay
      if (currentQ.type !== "number" && currentQ.type !== "multiSelect") {
        setTimeout(() => {
          handleNext();
        }, 300);
      }
    }
  };

  const handleInputChange = (e) => {
    const currentQ = questions[currentQuestion];
    setLocalData((prev) => ({
      ...prev,
      [currentQ.id]: e.target.value,
    }));
  };

  const handleNext = () => {
    if (currentQuestion < questions.length - 1) {
      // Skip cigarettesPerDay question if smokingHistory is 0
      if (currentQuestion === 1 && parseFloat(localData.smokingHistory) === 0) {
        // Skip from smokingHistory (index 1) to livedWithSmoker (index 3)
        setLocalData((prev) => ({
          ...prev,
          cigarettesPerDay: 0, // Set to 0 since they don't smoke
        }));
        setCurrentQuestion(3); // Skip to livedWithSmoker
      } else {
        setCurrentQuestion(currentQuestion + 1);
      }
    } else {
      // Save all data and move to next page
      Object.keys(localData).forEach((key) => {
        let value = localData[key];

        // Convert numeric fields to numbers
        if (
          (key === "smokingHistory" || key === "cigarettesPerDay") &&
          value !== ""
        ) {
          value = parseFloat(value);
        }

        updateSurveyData(key, value);
      });
      onNext();
    }
  };

  const handleHeaderBack = () => {
    // Header back button goes to SurveyPage3
    navigate("/survey/3");
  };

  const handleNavigationBack = () => {
    // Navigation back button goes to previous question within the page
    if (currentQuestion > 0) {
      // Skip cigarettesPerDay question when going back if smokingHistory is 0
      if (currentQuestion === 3 && parseFloat(localData.smokingHistory) === 0) {
        // Skip from livedWithSmoker (index 3) back to smokingHistory (index 1)
        setCurrentQuestion(1);
      } else {
        setCurrentQuestion(currentQuestion - 1);
      }
    } else {
      // If on first question, go to previous page
      if (onNavigationBack) {
        onNavigationBack();
      }
    }
  };

  const currentQ = questions[currentQuestion];
  const isAnswered =
    localData[currentQ.id] !== undefined &&
    localData[currentQ.id] !== null &&
    (Array.isArray(localData[currentQ.id])
      ? localData[currentQ.id].length > 0
      : currentQ.type === "number" 
        ? localData[currentQ.id] !== ""
        : localData[currentQ.id].toString().trim() !== "");
  
  // Calculate progress accounting for skipped question
  const skipCigarettesQuestion = parseFloat(localData.smokingHistory) === 0;
  const totalQuestionsToShow = skipCigarettesQuestion ? questions.length - 1 : questions.length;
  
  // Map actual question index to display position
  const getDisplayPosition = (index) => {
    if (!skipCigarettesQuestion) return index + 1;
    // When skipping cigarettes question (index 2)
    if (index < 2) return index + 1;
    if (index === 2) return 2; // cigarettes (won't be shown but just in case)
    return index; // indices 3,4 become positions 3,4
  };
  
  const displayPosition = getDisplayPosition(currentQuestion);
  const isLastQuestion = currentQuestion === questions.length - 1;

  const surveyConfig = {
    introText: t("survey.page4.intro"),
    questionText: currentQ.question,
    options: currentQ.type === "multiSelect" ? [] : currentQ.options || [],
    infoSections: [
      {
        title: t("survey.page4.whyThisMatters"),
        content: currentQ.info,
      },
      {
        title: t("survey.page4.noJudgmentZone"),
        content: t("survey.page4.noJudgmentContent"),
      },
    ],
  };

  return (
    <div className="survey-page-4">
      <SurveyTemplate
        introText={surveyConfig.introText}
        questionText={surveyConfig.questionText}
        options={
          currentQ.type === "number" || currentQ.type === "multiSelect"
            ? []
            : currentQ.options || []
        }
        infoSections={surveyConfig.infoSections}
        onOptionSelect={handleOptionSelect}
        selectedOption={localData[currentQ.id]}
        onBack={handleHeaderBack}
        infoPanelLayout="collapsible"
        customInput={
          currentQ.type === "number" ? (
            <div className="custom-input-wrapper">
              <input
                type="number"
                value={localData[currentQ.id]}
                onChange={handleInputChange}
                placeholder={currentQ.placeholder}
                className="option-input"
                min="0"
                max="100"
                step={["cigarettesPerDay", "smokingHistory"].includes(currentQ.id) ? "1" : "0.1"}
              />
              <span className="unit-label">{currentQ.unit}</span>
            </div>
          ) : currentQ.type === "multiSelect" ? (
            <div className="disease-selection-wrapper">
              <div className="disease-options-grid">
                {currentQ.options.map((disease) => (
                  <button
                    key={disease.value}
                    className={`disease-option-button ${
                      localData.chronicDiseases.includes(disease.value)
                        ? "selected"
                        : ""
                    }`}
                    onClick={() => handleOptionSelect(disease.value)}
                  >
                    <span>{disease.label}</span>
                    {localData.chronicDiseases.includes(disease.value) && (
                      <span className="checkmark">✓</span>
                    )}
                  </button>
                ))}
              </div>

              <div className="selection-summary">
                {localData.chronicDiseases.length > 0 ? (
                  <p className="selected-count">
                    {t("survey.page4.selectionSummary.selected", {
                      count: localData.chronicDiseases.length,
                    })}
                  </p>
                ) : (
                  <p className="no-selection">
                    {t("survey.page4.selectionSummary.none")}
                  </p>
                )}
              </div>
            </div>
          ) : null
        }
        progressContainer={
          <div className="progress-container">
            {/* Section Progress */}
            <div className="section-progress">
              <div className="section-indicators">
                <div
                  className={`section-indicator ${
                    currentSection >= 1 ? "active" : ""
                  }`}
                >
                  <span className="section-number">1</span>
                  <span className="section-label">
                    {t("survey.sections.demographics")}
                  </span>
                </div>
                <div
                  className={`section-indicator ${
                    currentSection >= 2 ? "active" : ""
                  }`}
                >
                  <span className="section-number">2</span>
                  <span className="section-label">
                    {t("survey.sections.physical")}
                  </span>
                </div>
                <div
                  className={`section-indicator ${
                    currentSection >= 3 ? "active" : ""
                  }`}
                >
                  <span className="section-number">3</span>
                  <span className="section-label">
                    {t("survey.sections.family")}
                  </span>
                </div>
                <div
                  className={`section-indicator ${
                    currentSection >= 4 ? "active" : ""
                  }`}
                >
                  <span className="section-number">4</span>
                  <span className="section-label">
                    {t("survey.sections.symptoms")}
                  </span>
                </div>
                <div
                  className={`section-indicator ${
                    currentSection >= 5 ? "active" : ""
                  }`}
                >
                  <span className="section-number">5</span>
                  <span className="section-label">
                    {t("survey.sections.lifestyle")}
                  </span>
                </div>
              </div>
            </div>

            {/* Question Progress */}
            <div className="question-progress">
              <div className="progress-bar">
                <div
                  className="progress-fill"
                  style={{
                    width: `${
                      ((currentQuestion + 1) / questions.length) * 100
                    }%`,
                    height: `${
                      ((currentQuestion + 1) / questions.length) * 100
                    }%`,
                  }}
                ></div>
              </div>
              <span className="progress-text">
                {t("survey.progress", {
                  current: currentQuestion + 1,
                  total: questions.length,
                })}
              </span>
            </div>
          </div>
        }
        navigationButtons={
          <div className="navigation-buttons">
            <button
              className="nav-button back-button"
              onClick={handleNavigationBack}
            >
              {t("common.back")}
            </button>
            <button
              className="nav-button next-button"
              onClick={handleNext}
              disabled={!isAnswered}
            >
              {isLastQuestion ? t("common.continue") : t("common.next")}
            </button>
          </div>
        }
      />
    </div>
  );
};

export default SurveyPage4;
